// Board: drag between status columns (changes status) or within one (reorders).
// Optimistic with undo. Contributors can drag only the cards they own.
import { useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MessageSquare, Paperclip, ShieldAlert } from 'lucide-react';
import {
  isOverdue,
  rankBetween,
  STATUS_LABEL,
  STATUS_ORDER,
  useTaskActions,
  type Task,
  type TaskStatus,
  type TrackerData,
} from '@/lib/tracker';
import { cn } from '@/lib/utils';
import { Avatar, DueDate, PhaseChip } from '@/components/tracker/bits';
import { useOpenTask } from '@/components/tracker/TaskPanel';

interface Props {
  programId: string;
  data: TrackerData;
  tasks: Task[];
  canDrag: (t: Task) => boolean;
  swimlanes: boolean;
}

const COLUMN_TINT: Record<TaskStatus, string> = {
  NOT_STARTED: 'var(--subtle)',
  IN_PROGRESS: 'var(--brand)',
  BLOCKED: 'var(--bad)',
  IN_REVIEW: 'var(--warn)',
  DONE: 'var(--ok)',
};

export function Board({ programId, data, tasks, canDrag, swimlanes }: Props) {
  const lanes = swimlanes
    ? data.phases
        .map((p) => ({ id: p.id, label: `${p.number}. ${p.name}`, tasks: tasks.filter((t) => t.phaseId === p.id) }))
        .filter((l) => l.tasks.length)
    : [{ id: 'all', label: '', tasks }];
  return (
    <div className="flex flex-col gap-5">
      {lanes.map((l) => (
        <div key={l.id}>
          {swimlanes && <h3 className="mb-2 text-[13px] font-semibold">{l.label}</h3>}
          <Lane laneId={l.id} programId={programId} data={data} tasks={l.tasks} canDrag={canDrag} />
        </div>
      ))}
      {!tasks.length && <p className="py-10 text-center text-sm text-muted">No tasks match these filters.</p>}
    </div>
  );
}

function Lane({ laneId, programId, data, tasks, canDrag }: Omit<Props, 'swimlanes'> & { laneId: string }) {
  const { move } = useTaskActions(programId);
  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const columns = useMemo(() => {
    const by: Record<TaskStatus, Task[]> = { NOT_STARTED: [], IN_PROGRESS: [], BLOCKED: [], IN_REVIEW: [], DONE: [] };
    for (const t of [...tasks].sort((a, b) => (a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : 0))) by[t.status].push(t);
    return by;
  }, [tasks]);

  const colId = (s: TaskStatus) => `${laneId}:${s}`;
  const statusOf = (id: string): TaskStatus | null => {
    if (id.startsWith(`${laneId}:`)) return id.split(':')[1] as TaskStatus;
    return tasks.find((t) => t.id === id)?.status ?? null;
  };

  function onDragEnd(e: DragEndEvent) {
    setActiveId(null);
    const task = tasks.find((t) => t.id === e.active.id);
    if (!task || !e.over) return;
    const overId = String(e.over.id);
    const target = statusOf(overId);
    if (!target) return;
    const col = columns[target].filter((t) => t.id !== task.id);
    // Drop on a card: take its place. Drop on the column: go to the end.
    let index = col.length;
    if (!overId.startsWith(`${laneId}:`)) {
      const i = col.findIndex((t) => t.id === overId);
      if (i >= 0) {
        const fromAbove =
          columns[target].findIndex((t) => t.id === task.id) < columns[target].findIndex((t) => t.id === overId);
        index = target === task.status && fromAbove ? i + 1 : i;
      }
    }
    const after = col[index - 1] ?? null;
    const before = col[index] ?? null;
    if (
      target === task.status &&
      after?.id === columns[target][columns[target].indexOf(task) - 1]?.id &&
      before?.id === columns[target][columns[target].indexOf(task) + 1]?.id
    )
      return;
    move.mutate({
      id: task.id,
      status: target !== task.status ? target : undefined,
      afterId: after?.id ?? null,
      beforeId: before?.id ?? null,
      rank: rankBetween(after?.rank, before?.rank),
    });
  }

  const active = tasks.find((t) => t.id === activeId);
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(e: DragStartEvent) => setActiveId(String(e.active.id))}
      onDragCancel={() => setActiveId(null)}
      onDragEnd={onDragEnd}
    >
      <div className="grid grid-cols-5 gap-3 overflow-x-auto pb-1" style={{ minWidth: 1050 }}>
        {STATUS_ORDER.map((s) => (
          <Column key={s} id={colId(s)} status={s} tasks={columns[s]} data={data} canDrag={canDrag} />
        ))}
      </div>
      <DragOverlay dropAnimation={null}>{active && <Card task={active} data={data} overlay />}</DragOverlay>
    </DndContext>
  );
}

function Column({
  id,
  status,
  tasks,
  data,
  canDrag,
}: {
  id: string;
  status: TaskStatus;
  tasks: Task[];
  data: TrackerData;
  canDrag: (t: Task) => boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex min-h-40 flex-col rounded-lg border border-border bg-surface-2/50 transition-colors',
        isOver && 'border-ring/50 bg-brand-soft/30',
      )}
    >
      <div
        className="flex items-center gap-2 rounded-t-lg border-b border-border px-3 py-2"
        style={{
          borderTop: `3px solid ${COLUMN_TINT[status]}`,
          background: `color-mix(in srgb, ${COLUMN_TINT[status]} 10%, transparent)`,
        }}
      >
        <span className="size-2 rounded-full" style={{ background: COLUMN_TINT[status] }} />
        <span className="text-[12.5px] font-semibold" style={{ color: COLUMN_TINT[status] }}>
          {STATUS_LABEL[status]}
        </span>
        <span className="ml-auto text-xs tabular text-subtle">{tasks.length}</span>
      </div>
      <SortableContext items={tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
        <div className="flex flex-1 flex-col gap-2 p-2">
          {tasks.map((t) => (
            <SortableCard key={t.id} task={t} data={data} disabled={!canDrag(t)} />
          ))}
        </div>
      </SortableContext>
    </div>
  );
}

function SortableCard({ task, data, disabled }: { task: Task; data: TrackerData; disabled: boolean }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
    disabled,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(isDragging && 'opacity-30')}
      {...attributes}
      {...listeners}
    >
      <Card task={task} data={data} draggable={!disabled} />
    </div>
  );
}

function Card({
  task,
  data,
  overlay,
  draggable,
}: {
  task: Task;
  data: TrackerData;
  overlay?: boolean;
  draggable?: boolean;
}) {
  const open = useOpenTask();
  const phase = data.phases.find((p) => p.id === task.phaseId);
  const owner = data.members.find((m) => m.userId === task.ownerId);
  const overdue = isOverdue(task, data.today);
  return (
    <button
      type="button"
      onClick={() => open(task.id)}
      className={cn(
        'w-full rounded-md border border-border bg-surface p-2.5 text-left shadow-xs transition-shadow hover:border-border-strong hover:shadow-sm',
        draggable ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer',
        overlay && 'rotate-1 shadow-lg',
      )}
    >
      <p className="line-clamp-3 text-[13px] leading-snug">{task.title}</p>
      <div className="mt-2 flex items-center gap-1.5 text-[11px]">
        {phase && <PhaseChip number={phase.number} name={phase.name} />}
        <DueDate date={task.dueDate} overdue={overdue} className="text-[11px]" />
        <span className="ml-auto flex items-center gap-1.5 text-subtle">
          {task.raidOpen && <ShieldAlert className="size-3.5 text-warn" aria-label="Linked to an open RAID item" />}
          {task.attachments > 0 && (
            <span className="flex items-center gap-0.5">
              <Paperclip className="size-3" />
              {task.attachments}
            </span>
          )}
          {task.comments > 0 && (
            <span className="flex items-center gap-0.5">
              <MessageSquare className="size-3" />
              {task.comments}
            </span>
          )}
          <Avatar name={owner?.name} size={20} />
        </span>
      </div>
      {task.checklistTotal > 0 && (
        <div className="mt-2 h-1 overflow-hidden rounded-full bg-surface-3">
          <div className="h-full bg-brand" style={{ width: `${(task.checklistDone / task.checklistTotal) * 100}%` }} />
        </div>
      )}
    </button>
  );
}
